import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import { get, updateToken } from "../../../../helpers/api_helper";
import { useSelector } from "react-redux";
import { Form, Spinner } from "react-bootstrap";

const EarningsChart = () => {
  const token = useSelector((state) => state.login.token);
  const [data, setData] = useState([]);
  const [days, setDays] = useState(7);
  const [chartData, setChartData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [percentageChange, setPercentageChange] = useState(0);

  useEffect(() => {
    if (token) {
      updateToken(token);
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await get("/graph/earn-subs");
        if (response?.success) {
          setData(response?.data);
        } else {
          console.error("Error: API response not successful");
          setError("Failed to fetch data.");
        }
      } catch (error) {
        console.error("Error fetching earnings data:", error);
        setError("Error fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  useEffect(() => {
    const filteredData = data?.slice(-days);
    const labels = filteredData?.map((entry) => entry?.date);
    const earnings = filteredData?.map((entry) => entry?.earnings);

    const currentTotal = earnings?.reduce((acc, value) => acc + value, 0);
    const previousData = data?.slice(-days * 2, -days);
    const previousEarnings = previousData?.map((entry) => entry?.earnings);
    const previousTotal = previousEarnings?.reduce(
      (acc, value) => acc + value,
      0
    );

    const change = previousTotal
      ? ((currentTotal - previousTotal) / previousTotal) * 100
      : 0;

    setTotalEarnings(currentTotal.toFixed(2));
    setPercentageChange(change.toFixed(2));

    setChartData({
      labels,
      datasets: [
        {
          label: "Earnings",
          data: earnings,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    });
  }, [data, days]);

  return (
    <div style={{ height: "400px", width: "100%" }}>
      <div style={{ marginBottom: "10px" }}>
        <Form.Select
          className="small-select"
          id="daysSelect"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>Last 7 days</option>
          <option value={15}>Last 15 days</option>
          <option value={30}>Last 30 days</option>
        </Form.Select>
      </div>

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", color: "red" }}>
          <p>{error}</p>
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: "center" }}>
          <p>No records found.</p>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <p>Accounting</p>
            <p style={{ fontSize: "24px", fontWeight: "bold" }}>
              ${totalEarnings}
            </p>
            <p style={{ color: percentageChange >= 0 ? "green" : "red" }}>
              {percentageChange >= 0 ? "+" : ""}
              {percentageChange}% than last week
            </p>
          </div>
          <Bar
            data={chartData}
            options={{
              responsive: true,
              scales: {
                x: {
                  title: {
                    display: true,
                    text: "Date",
                  },
                },
                y: {
                  title: {
                    display: true,
                    text: "Earnings",
                  },
                  beginAtZero: true,
                },
              },
            }}
          />
        </>
      )}
    </div>
  );
};

export default EarningsChart;
